import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { settingsApi } from '@/api/settings';
import { ADMIN_EMAIL } from '@/lib/supabase';
import { Button, EmptyState, Input, Loading } from '@/components/ui';

export default function AdminAccessSection() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['settings-admin-users'],
    queryFn: settingsApi.listAdminUsers,
    staleTime: 30_000,
  });

  const addMutation = useMutation({
    mutationFn: settingsApi.addAdminUser,
    onSuccess: async () => {
      setEmail('');
      setError('');
      await queryClient.invalidateQueries({ queryKey: ['settings-admin-users'] });
    },
    onError: (mutationError) => {
      setError((mutationError as Error).message);
    },
  });

  const removeMutation = useMutation({
    mutationFn: settingsApi.removeAdminUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings-admin-users'] });
    },
  });

  return (
    <section className="card p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-amber-500/10 p-2 text-amber-400">
          <Lock className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-ink-primary">Admin access</h2>
          <p className="mt-1 text-sm text-ink-secondary">
            Current runtime allow-list still comes from <code className="text-gold">ADMIN_EMAIL</code>. The table
            below prepares the follow-up swap-over.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-navy-border bg-navy/30 p-4">
        <p className="text-xs uppercase tracking-wide text-ink-muted">Current hardcoded allow-list</p>
        <p className="mt-2 font-mono text-sm text-ink-primary">{ADMIN_EMAIL}</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <Input
          label="Admin email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@example.com"
        />
        <Button
          type="button"
          onClick={() => void addMutation.mutateAsync(email)}
          loading={addMutation.isPending}
        >
          <Plus className="h-4 w-4" />
          Add admin user
        </Button>
      </div>

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {isLoading ? (
        <Loading text="Loading admin users..." />
      ) : !data?.length ? (
        <EmptyState
          icon={<Lock className="h-8 w-8" />}
          title="No admin users"
          description="The table is empty. The hardcoded ADMIN_EMAIL still controls access today."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-navy-border text-left text-xs text-ink-muted">
                <th className="py-3 pr-4">Email</th>
                <th className="py-3 pr-4">Created</th>
                <th className="py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-border">
              {data.map((user) => (
                <tr key={user.email}>
                  <td className="py-3 pr-4 font-mono text-xs text-ink-primary">{user.email}</td>
                  <td className="py-3 pr-4 text-ink-secondary">{new Date(user.created_at).toLocaleString()}</td>
                  <td className="py-3">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void removeMutation.mutateAsync(user.email)}
                      disabled={removeMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
